// What the web client knows: one state, which the server's messages and the user's acts change, and
// which the page shows (ui/). Whatever changes it calls `changed()`: the page is drawn again once, after
// all of the changes of the moment — in a microtask, not an animation frame, so that a page out of sight
// is drawn too (its timers and frames are throttled, not its messages).
import type { Codec } from "./binary.ts";
import type { Board } from "./board.ts";
import type { Key } from "./identity.ts";
import type { ClientMsg, Server, ServerMsg } from "./protocol.ts";

/** A meeting — a "пространство" — the user is in: id, number (of its community and first channels), host
 * and title. */
export type Meeting = { id: string; number: number; host: number; title: string };

/** A message of a text channel's chat; an image of one. */
export type Message = Server<"message">;
export type FileRef = Message["files"][number];

/** An image of a message the user writes — picked, pasted or dropped — made ready (images.ts) and
 * uploaded (chat.ts): its name, its picture here (an object URL), its pixels and bytes, how its upload
 * goes, and once it is up the ids the server gave it and its smaller copy ("": none). */
export type Attachment = {
  key: number;
  name: string;
  url: string;
  width: number;
  height: number;
  size: number;
  status: "preparing" | "uploading" | "done" | "failed";
  progress: number;
  file: string;
  preview: string;
  error: string;
};

/** What the user writes in a text channel and has not sent: the text, the message it answers, its
 * images, and the users named in it (`@name` as written, and the user). */
export type Draft = { text: string; reply: Message | null; files: Attachment[]; named: [string, number][] };

/** A message of the user's the server has not confirmed: its channel, text, images and the message it
 * answers; `waiting` while its images go up. */
export type Pending = {
  channel: number;
  text: string;
  files: Attachment[];
  reply: Message | null;
  waiting: boolean;
};

/** What a dialog over the page asks: to delete a message; the user's name, picture and access code; or
 * whom to invite — the meeting's link. */
export type Dialog = { kind: "delete"; message: Message } | { kind: "profile" } | { kind: "invite" };

/** A channel of the meeting: its number, kind and name ("": the first one of its kind). */
export type Channel = Server<"layout">["channels"][number];

/** What the middle shows: a text channel's chat, or a voice channel — its room and transcripts. */
export type View = { kind: "text" | "voice"; channel: number };

/** Who is in a voice channel, whose camera is on and who shows a screen (the server says). */
export type VoiceUsers = { users: number[]; cameras: number[]; screens: number[] };

/** A line of a transcript: the words of a phrase so far, or its last (`final`). */
export type Line = Server<"line">;

/** A transcript of a voice channel, as the list says it: number, when it began and its last line was
 * said, and how many lines it has. */
export type TranscriptSession = Server<"transcripts">["sessions"][number];

/** The transcripts of a voice channel as the page knows them: which is written now (0: none), the list
 * (null till it comes), the one shown (0: the list) and its lines by number — the one written comes
 * line by line — where the oldest of them lies (-1: none came yet), whether there are none older,
 * whether a page is asked for, and whether all of them are, to be downloaded. */
export type Transcripts = {
  writing: number;
  sessions: TranscriptSession[] | null;
  shown: number;
  lines: Map<number, Line>;
  from: number;
  all: boolean;
  asking: boolean;
  saving: boolean;
};

export function noTranscripts(): Transcripts {
  return {
    writing: 0,
    sessions: null,
    shown: 0,
    lines: new Map(),
    from: -1,
    all: false,
    asking: false,
    saving: false,
  };
}

/** A pair of tracks the call takes — the sound and video of one other member, whose voice session the
 * server says (`slots`) — and what plays them. */
export type Slot = {
  audio: RTCRtpTransceiver;
  video: RTCRtpTransceiver;
  session: number;
  audioEl: HTMLAudioElement | null;
  videoEl: HTMLVideoElement | null;
};

/** A track of sound alone the call takes — a member's who speaks and has no pair, for its tile is not
 * shown; whose, the server says (`slots`) — and what plays it. */
export type VoiceSlot = {
  audio: RTCRtpTransceiver;
  session: number;
  audioEl: HTMLAudioElement | null;
};

/** A track of video only the call takes — a screen another member shows, whose voice session the server
 * says (`slots`) — and what plays it. */
export type ScreenSlot = {
  video: RTCRtpTransceiver;
  session: number;
  videoEl: HTMLVideoElement | null;
};

/** The user's screen shown (call.ts): the connection of its own it goes on, its track, what stops it
 * besides the track (a test's drawing), its voice session (-1 till the server answers), and its picture
 * on the page. */
export type Share = {
  pc: RTCPeerConnection;
  track: MediaStreamTrack;
  stop: (() => void) | null;
  session: number;
  self: HTMLVideoElement | null;
};

/** How loud a track is, 0 to 1. */
export type Meter = { level(): number; close(): void };

/** The user's sound and video (call.ts): in voice channel `channel` of community `community`. */
export type Call = {
  pc: RTCPeerConnection;
  community: number;
  channel: number;
  /** The browser's voice session in the room (-1 till the server answers). */
  session: number;
  mic: MediaStreamTrack | null;
  micStop: (() => void) | null;
  /** The microphone goes through RNNoise. */
  denoised: boolean;
  cam: MediaStreamTrack | null;
  camStop: (() => void) | null;
  micTx: RTCRtpTransceiver | null;
  camTx: RTCRtpTransceiver | null;
  slots: Slot[];
  voices: VoiceSlot[];
  screens: ScreenSlot[];
  /** The members whose video the server does not send now: the way to the browser does not take it. */
  paused: number[];
  /** The user's screen, while the user shows it. */
  share: Share | null;
  level: Meter | null;
  timer: number;
  /** The user's own picture. */
  self: HTMLVideoElement | null;
  /** The browser would not play the others' sound yet (`unlockAudio`). */
  locked: boolean;
};

/** What a phone shows: the middle (a chat, a voice channel's page, the call's transcript), the call's
 * tiles, or the channels and participants. */
export type Pane = "chat" | "voice" | "members";

/** How the call shows its people: all in tiles, a page at a time, or the one who speaks large and the
 * others in a row below. */
export type CallView = "grid" | "speaker";

/** Reconnecting: the first wait, ms. */
export const RETRY = 500;

export const state = {
  /** The user's number in the meeting the user is in (the server gives it), and the name the user
   * gave ("": none); the name others see in the meeting (the user's, or a guest's); the user's picture
   * (a file's id, "": none). */
  user: 0,
  name: "",
  shown: "",
  avatar: "",
  /** The account: its secret, access code and key (identity.ts); its meetings (the server's list). */
  secret: new Uint8Array(0) as Uint8Array,
  code: "",
  key: null as Key | null,
  mine: [] as Server<"mine">["meetings"],
  codecs: null as { client: Codec<ClientMsg>; server: Codec<ServerMsg> } | null,
  ws: null as WebSocket | null,
  /** Signed in on the connection: requests go. */
  up: false,
  retry: RETRY,
  meeting: null as Meeting | null,
  /** The id of a meeting asked for (its link) and not open yet; whether the user is still to say to
   * go in (the name first); whether a meeting is being made. */
  wanted: null as string | null,
  joining: false,
  creating: false,
  /** The meeting's channels and moderators (the server says); what the middle shows; the channels the
   * page opened — it hears them: the one shown, and the call's. */
  layout: { channels: [] as Channel[], moderators: [] as number[] },
  view: { kind: "text", channel: 0 } as View,
  opened: new Set<number>(),
  /** The messages of the text channel shown by number, the user's own not confirmed yet by nonce, the
   * newest number, whether older ones are being asked for, whether there are none older, the message
   * the user edits (0: none), the one to go to and show (0: none) and one a phone shows the acts of. */
  messages: new Map<number, Message>(),
  pending: new Map<number, Pending>(),
  last: 0,
  asking: false,
  all: false,
  editing: 0,
  jump: 0,
  selected: 0,
  /** What the user writes in each text channel. */
  drafts: new Map<number, Draft>(),
  /** The messages naming the user in channels not shown, by channel, since the user looked last. */
  mentions: new Map<number, number>(),
  /** The images shown whole over the page, and which; a dialog over it. */
  lightbox: null as { files: FileRef[]; index: number } | null,
  dialog: null as Dialog | null,
  online: new Set<number>(),
  /** The names and pictures of the meeting's users, as the server said; those asked for. */
  names: new Map<number, string>(),
  avatars: new Map<number, string>(),
  asked: new Set<number>(),
  typists: [] as number[],
  status: "",
  error: false,
  /** What a phone shows. */
  pane: "chat" as Pane,
  /** A wide screen in the call shows the transcript beside the tiles. */
  panel: true,
  /** Who is in each voice channel (the server says, in it or not), and its transcripts. */
  voices: new Map<number, VoiceUsers>(),
  transcripts: new Map<number, Transcripts>(),
  /** The call (`joinCall`), null when the user has no sound; the voice channel the user wants to be in
   * (-1: none; the call starts again after a reconnection), the camera on, the microphone off, the
   * others' sound off. */
  call: null as Call | null,
  wantCall: -1,
  camOn: false,
  micOff: false,
  deafened: false,
  /** The voice room: its sessions, their users, whose camera is on, and which are screens. */
  room: { sessions: [] as number[], users: [] as number[], cameras: [] as number[], screens: [] as number[] },
  /** The users who speak now; the one of the others who speaks large (`speaker` view) — who spoke last
   * (-1: none yet) — and when that one last spoke (ms). */
  speaking: new Set<number>(),
  speaker: -1,
  speakerAt: 0,
  /** How the call shows its people, and the page of tiles shown. */
  callView: "grid" as CallView,
  page: 0,
  /** The boards of the call (boards.ts), by number, and the one the stage shows large (0: none chosen). */
  boards: new Map<number, Board>(),
  boardShown: 0,
  /** The chat's view goes to the newest message at the next drawing (the user wrote). */
  toEnd: false,
};

export type State = typeof state;

const listeners = new Set<() => void>();
let scheduled = false;

/** The state changed: the page is drawn again, once for all the changes of the moment. */
export function changed(): void {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(() => {
    scheduled = false;
    for (const l of listeners) l();
  });
}

/** Calls `l` whenever the state changed; what stops that. */
export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** A line for the user: what happens, or what went wrong. */
export function show(text: string, error = false): void {
  state.status = text;
  state.error = error;
  changed();
}

/** The name of user `id` in the meeting. */
export function nameOf(id: number): string {
  if (id === state.user) return state.shown || state.name;
  return state.names.get(id) ?? `участник ${id % 10000}`;
}

/** The picture of user `id` (a file's id), "" if none. */
export function avatarOf(id: number): string {
  if (id === state.user) return state.avatar;
  return state.avatars.get(id) ?? "";
}

/** The address of file `id` of the chat on the web port. */
export function fileUrl(id: string): string {
  return `/f/${id}`;
}

/** Whether the user is in meeting `number`. */
export function inMeeting(number: number): boolean {
  return state.meeting !== null && state.meeting.number === number;
}

/** Whether channel `number` is one of the meeting's. */
export function ofMeeting(number: number): boolean {
  return state.meeting !== null && state.layout.channels.some((c) => c.number === number);
}

/** The channel of number `number` and kind `kind`, if the meeting has it. */
export function channelOf(number: number, kind: "text" | "voice"): Channel | null {
  return state.layout.channels.find((c) => c.number === number && c.kind === kind) ?? null;
}

/** What a new meeting — a space — is called. */
export const MEETING_TITLE = "Пространство";
/** What new meetings were called by the versions before ("Встреча", then "Переговорка"). No one could call
 * one otherwise: such a meeting is called as a new one is now. */
const OLD_TITLES = ["Встреча", "Переговорка"];

/** The title of a meeting as the page shows it: its own, or a new meeting's — for a meeting of the versions
 * before too. */
export function meetingTitle(title: string): string {
  return title === "" || OLD_TITLES.includes(title) ? MEETING_TITLE : title;
}

/** The name of channel `c`: its own, or its kind's if it has none (a meeting's first two). */
export function channelName(c: Channel): string {
  return c.name !== "" ? c.name : c.kind === "text" ? "общий" : "голосовой";
}

/** Whether the user is the meeting's host, or a moderator of it (who controls its transcripts). */
export function isHost(): boolean {
  return state.meeting !== null && state.meeting.host === state.user;
}

export function isModerator(id: number): boolean {
  return state.layout.moderators.includes(id);
}

/** The transcripts of voice channel `channel`, made the first time. */
export function transcriptsOf(channel: number): Transcripts {
  let t = state.transcripts.get(channel);
  if (t === undefined) {
    t = noTranscripts();
    state.transcripts.set(channel, t);
  }
  return t;
}
